import { getUserAlias, getMidwayCredentials } from './midway';
import { AwsClient } from 'aws4fetch';
import { getEnvironmentConfig } from '../configurations/config';

// Lambda Function URL for BRASS authorization - determined by stage
const getBrassLambdaUrl = () => {
  const config = getEnvironmentConfig();
  return config.brassLambdaUrl;
};

const BINDLE_LOCKS = {
  // Main application access (Dashboard, Review Mappings, Mapping Statistics)
  // Only Nexus-eng, gss-csa-team, and AWS-SAS-Internal should have access
  MAIN_APP_ACCESS: 'amzn1.bindle.resource.mq5tmrj7td6q6hjbtn5a',
  // Community Labeling page access
  // Only designated community labelers should have access
  COMMUNITY_LABELING: 'amzn1.bindle.resource.ojqgxp6kxo5xuiz2jtgq', 
};

/**
 * Page to Bindle Lock mapping
 * Maps page paths to the Bindle Lock IDs required to access them
 */
const PAGE_BINDLE_LOCKS: Record<string, string> = {
  '/dashboard': BINDLE_LOCKS.MAIN_APP_ACCESS,
  '/review': BINDLE_LOCKS.MAIN_APP_ACCESS,
  '/stats': BINDLE_LOCKS.MAIN_APP_ACCESS,
  '/labels': BINDLE_LOCKS.COMMUNITY_LABELING,
  '/mapping-jobs': BINDLE_LOCKS.MAIN_APP_ACCESS,
  '/explorer': BINDLE_LOCKS.MAIN_APP_ACCESS,
  '/ingest': BINDLE_LOCKS.MAIN_APP_ACCESS,
};

/**
 * Check if a user can unlock a specific Bindle Lock using BRASS
 * @param bindleLockId - The Bindle Lock ID to check
 * @param userAlias - The user's alias (optional, defaults to current user)
 * @returns Promise<boolean> - true if user can unlock, false otherwise
 */
export const canUnlockBindleLock = async (
  bindleLockId: string,
  userAlias?: string
): Promise<boolean> => {
  try {
    const alias = userAlias || await getUserAlias();
    
    // Get Midway credentials for SigV4 signing
    const credentials = await getMidwayCredentials();
    
    // Create AWS client for signing requests
    const aws = new AwsClient({
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
      region: 'us-west-2',
      service: 'lambda',
    });
    
    const requestBody = JSON.stringify({
      bindleLockId,
      userAlias: alias,
    });
    
    // Sign and send the request
    const response = await aws.fetch(getBrassLambdaUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: requestBody,
    });

    if (!response.ok) {
      console.error('BRASS authorization check failed:', response.statusText);
      return false;
    }

    const result = await response.json();
    return result.authorized === true;
  } catch (error) {
    console.error('Error checking Bindle Lock authorization:', error);
    // Fail closed - deny access on error
    return false;
  }
};

/**
 * Check if user has access to a specific page based on Bindle Locks
 * @param pagePath - The page path to check (e.g., '/labels')
 * @param userAlias - The user's alias (optional, defaults to current user)
 * @returns Promise<boolean> - true if user can access the page, false otherwise
 */
export const canAccessPage = async (
  pagePath: string,
  userAlias?: string
): Promise<boolean> => {
  const bindleLockId = PAGE_BINDLE_LOCKS[pagePath];
  
  // If no Bindle Lock is configured for this page, allow access by default
  if (!bindleLockId) {
    return true;
  }
  
  // Check if the Bindle Lock ID is still a placeholder
  if (bindleLockId.includes('PLACEHOLDER')) {
    console.warn(
      `Bindle Lock for page ${pagePath} is not configured. ` +
      'Please create a Bindle Lock and update the BINDLE_LOCKS constant.'
    );
    // During development, allow access if Bindle Lock is not configured
    // In production, you might want to deny access instead
    return true;
  }
  
  return canUnlockBindleLock(bindleLockId, userAlias);
};

/**
 * Get all accessible pages for a user
 * @param userAlias - The user's alias (optional, defaults to current user)
 * @returns Promise<string[]> - Array of page paths the user can access
 */
export const getAccessiblePages = async (userAlias?: string): Promise<string[]> => {
  const pages = Object.keys(PAGE_BINDLE_LOCKS);
  const accessChecks = await Promise.all(
    pages.map(async (page) => ({
      page,
      canAccess: await canAccessPage(page, userAlias),
    }))
  );
  
  return accessChecks
    .filter(({ canAccess }) => canAccess)
    .map(({ page }) => page);
};

/**
 * Filter navigation items based on user's Bindle Lock permissions
 * @param navigationItems - Array of navigation items
 * @param userAlias - The user's alias (optional, defaults to current user)
 * @returns Promise<any[]> - Filtered navigation items
 */
export const filterNavigationByBindleLocks = async (
  navigationItems: any[],
  userAlias?: string
): Promise<any[]> => {
  const accessiblePages = await getAccessiblePages(userAlias);
  
  const filteredSections = await Promise.all(
    navigationItems.map(async (section) => {
      if (section.items) {
        const filteredItems = section.items.filter((item: any) => {
          if (item.href) {
            // If page has a Bindle Lock requirement, check if it's accessible
            if (PAGE_BINDLE_LOCKS[item.href]) {
              return accessiblePages.includes(item.href);
            }
            // If no Bindle Lock requirement, allow access
            return true;
          }
          return true;
        });
        
        return {
          ...section,
          items: filteredItems,
        };
      }
      return section;
    })
  );
  
  // Remove sections that have no items left after filtering
  return filteredSections.filter((section) => {
    return !section.items || section.items.length > 0;
  });
};

/**
 * React hook to check page access
 * Usage: const canAccess = usePageAccess('/labels');
 */
export const usePageAccess = (pagePath: string) => {
  const [canAccess, setCanAccess] = React.useState<boolean | null>(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    const checkAccess = async () => {
      setLoading(true);
      const access = await canAccessPage(pagePath);
      setCanAccess(access);
      setLoading(false);
    };
    
    checkAccess();
  }, [pagePath]);
  
  return { canAccess, loading };
};

// Re-export React for the hook
import React from 'react';
